import { type ButtonProps, Button as RealButton, Typography } from '@alpine-iq/ui-library'

import { useBaseOverrides } from '@/hooks/useBaseOverrides'
import { useSessionStore } from '@/stores/useSessionStore'
import { cssNum, DefaultBrandStyle, setHexAlpha } from '@/utils'

export default function WidgetButton(props: ButtonProps & { isActive?: boolean }) {
  const isPrimary = props.variant === 'primary'
  const buttonKey = isPrimary ? 'buttonPrimary' : 'buttonSecondary'

  const widgetStyle = useSessionStore((state) => state.widgetSettings?.style)
  const { brandColor } = useBaseOverrides()

  const buttonTextColor = widgetStyle?.[buttonKey]?.color ?? DefaultBrandStyle?.[buttonKey]?.color
  const defaultButtonBG = isPrimary ? brandColor : DefaultBrandStyle?.[buttonKey]?.bg
  const buttonBG = widgetStyle?.[buttonKey]?.bg ?? defaultButtonBG

  const defaultButtonBorderColor = isPrimary ? brandColor : DefaultBrandStyle?.[buttonKey]?.borderColor
  const buttonBorderColor = widgetStyle?.[buttonKey]?.borderColor ?? defaultButtonBorderColor

  const defaultButtonBorderRadius = DefaultBrandStyle?.[buttonKey]?.borderRadius?.topLeft
  const widgetBorderRadius = widgetStyle?.[buttonKey]?.borderRadius
  const buttonBorderRadius = widgetBorderRadius?.isSet
    ? widgetBorderRadius.topLeft
    : widgetBorderRadius?.topLeft || defaultButtonBorderRadius

  const useBorderColor = props.isActive ? brandColor : buttonBorderColor

  const buttonColorHover = setHexAlpha(buttonBG ?? '', 0.9)
  const buttonBorderColorHover = isPrimary
    ? setHexAlpha(useBorderColor ?? '', 0.9)
    : setHexAlpha(brandColor ?? '', 0.5)

  const disabledButtonBG = setHexAlpha(buttonBG ?? '', 0.8)

  return (
    <RealButton
      css={`
        color: ${buttonTextColor};
        background-color: ${buttonBG};
        border-color: ${useBorderColor};
        border-radius: ${cssNum(buttonBorderRadius)};
        border-top-left-radius: ${cssNum(buttonBorderRadius)};
        border-top-right-radius: ${cssNum(buttonBorderRadius)};
        border-bottom-left-radius: ${cssNum(buttonBorderRadius)};
        border-bottom-right-radius: ${cssNum(buttonBorderRadius)};

        &:not(:disabled) {
          &:hover,
          &:active {
            background-color: ${buttonColorHover};
            border-color: ${buttonBorderColorHover};
          }

          &:focus,
          &:focus-visible,
          &:focus-within {
            background-color: ${buttonColorHover};
            border-color: ${props.isActive ? useBorderColor : buttonBorderColorHover} !important;
            border-style: solid;
            border-width: 2px;
            outline: none;
          }
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          background-color: ${disabledButtonBG} !important;
        }
      `}
      {...props}
      style={{
        maxWidth: 450,
        borderWidth: props.isActive ? '4px' : 2,
        ...props.style,
      }}
    >
      <Typography
        style={{
          overflow: 'visible',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          width: '100%',
          minWidth: 0,
          color: buttonTextColor,
          fontWeight: 600,
        }}
      >
        {props.children}
      </Typography>
    </RealButton>
  )
}
